import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../App';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';

function MessageViewer({ message, email, onClose }) {
  const [fullMessage, setFullMessage] = useState(message);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('text'); // 'text' or 'html'

  const loadFullMessage = useCallback(async () => {
    if (!message?._id || !email) return;
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}/email/${encodeURIComponent(email)}/messages/${message._id}`
      );
      setFullMessage(response.data);
    } catch (error) {
      console.error('Error loading full message:', error);
      toast.error('Failed to load message');
    } finally {
      setLoading(false);
    }
  }, [email, message?._id]);

  useEffect(() => {
    // Load full message if we only have partial data
    if (message && !message.html && message._id) {
      loadFullMessage();
    } else {
      setFullMessage(message);
    }
  }, [loadFullMessage, message]);

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const extractOTP = (text) => {
    // Try to find OTP codes (4-8 digit numbers)
    const otpMatch = text.match(/\b\d{4,8}\b/);
    return otpMatch ? otpMatch[0] : null;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-8 fade-in">
        <div className="text-center">
          <div className="spinner mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading message...</p>
        </div>
      </div>
    );
  }

  if (!fullMessage) {
    return null;
  }

  const otp = extractOTP(fullMessage.text);

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden fade-in">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-semibold text-lg truncate flex-1">{fullMessage.subject}</h2>
          <button
            onClick={onClose}
            className="ml-4 text-white hover:text-gray-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="text-sm opacity-90">
          <p>From: {fullMessage.fromName || fullMessage.from}</p>
          <p className="text-xs mt-1">
            {formatDistanceToNow(new Date(fullMessage.receivedAt), { addSuffix: true })}
          </p>
        </div>
      </div>

      {/* OTP Highlight */}
      {otp && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 m-4 rounded">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-yellow-800">OTP Detected</p>
              <p className="text-3xl font-bold text-yellow-600 mt-2">{otp}</p>
            </div>
            <button
              onClick={() => copyText(otp)}
              className="bg-yellow-600 text-white px-4 py-2 rounded hover:bg-yellow-700 transition-colors"
            >
              Copy OTP
            </button>
          </div>
        </div>
      )}

      {/* View Mode Toggle */}
      {fullMessage.html && (
        <div className="border-b border-gray-200 px-4 py-2 flex gap-2">
          <button
            onClick={() => setViewMode('text')}
            className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
              viewMode === 'text'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Text
          </button>
          <button
            onClick={() => setViewMode('html')}
            className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
              viewMode === 'html'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            HTML
          </button>
        </div>
      )}

      {/* Message Content */}
      <div className="p-6">
        {viewMode === 'html' && fullMessage.html ? (
          <div
            className="prose max-w-none"
            dangerouslySetInnerHTML={{ __html: fullMessage.html }}
          />
        ) : (
          <div className="whitespace-pre-wrap text-gray-800 font-mono text-sm bg-gray-50 p-4 rounded">
            {fullMessage.text || '(No content)'}
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 flex gap-3">
          <button
            onClick={() => copyText(fullMessage.text)}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Copy Text
          </button>
          <button
            onClick={() => copyText(fullMessage.from)}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded transition-colors flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            Copy From
          </button>
        </div>

        {/* Attachments */}
        {fullMessage.attachments && fullMessage.attachments.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-3">Attachments</h3>
            <div className="space-y-2">
              {fullMessage.attachments.map((attachment, index) => (
                <div
                  key={index}
                  className="bg-gray-50 p-3 rounded flex items-center justify-between"
                >
                  <div>
                    <p className="font-medium text-gray-800">{attachment.filename}</p>
                    <p className="text-sm text-gray-500">
                      {attachment.contentType} • {(attachment.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                  {attachment.url && (
                    <a
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:text-blue-700"
                    >
                      Download
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MessageViewer;

